import React, { Component } from 'react'
import { observer, inject } from 'mobx-react'
import { observable, action, runInAction, makeObservable } from 'mobx'
import queryString from 'query-string'
import classnames from 'classnames'
import withStyles, { ThemeProvider } from 'react-jss'

import { getCommissionRange, getStagesType } from 'utils/affiliatePrograms.utils'
import { getShopProductLink } from 'utils/helpers.utils'
import { copyTextToClipboard } from 'utils/dom.utils'
import { profile } from 'utils/profileHelper.utils'

import { AFFILIATE_PROGRAM_FORMS } from 'constants/affiliatePrograms.constants'
import { PROFILE_TYPES, ACTIVE_PROFILES } from 'constants/profile.constants'

import { NumberField, ErrorInfo, TextField } from '@elo-kit/components'

import { fetchItem } from 'shared/api/publisher.api'
import { join, checkPid } from 'shared/api/affiliateInvite.api'

import { signUp } from '../../../../app/api/user.api'

import embeddableItemStyles from '../../embeddableItemStyles'
import RegistrationForm from './RegistrationForm'

@inject('affiliateProgramsStore')
@observer
class EmbedForm extends Component {
  @observable formConfigs = {}
  @observable userData = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    termsAccepted: false,
  }
  @observable registrationOpened = false
  @observable affiliateProgram = {
    commission: [],
    affiliateProgramCommissions: [],
  }
  @observable loading = false
  @observable recaptcha = null

  constructor(props) {
    super(props)
    makeObservable(this)
  }

  componentDidMount() {
    this.fetchAffiliateProgram()
  }

  componentDidUpdate() {
    const {
      productLink: {
        affiliateNode = {},
        embedPrefs: { prid },
      },
    } = this.props
    const { affiliateProgramId } = affiliateNode
    const { id } = this.affiliateProgram || {}
    const programId = prid || affiliateProgramId
    const shouldRefetch = programId && id !== parseInt(programId)
    if (shouldRefetch) this.fetchAffiliateProgram()
  }

  handlePubIdSubmit = () => {
    checkPid(this.formConfigs.publisherId).then((resp) => {
      if (resp.success) return this.joinPublisher()
      this.changeFormConfigs('publishError', I18n.t('front.widgets.embed_form.not_found_publisher'))
    })
  }

  joinPublisher = () => {
    const {
      productLink: {
        affiliateNode = {},
        embedPrefs: { locale },
      },
    } = this.props
    const { publisherId: parentId } = affiliateNode

    const isPublisher = profile.profileType === ACTIVE_PROFILES.publisher
    const joinId = isPublisher ? parentId : null
    join(this.affiliateProgram.id, {
      joinId,
      publisherId: this.formConfigs.publisherId,
      headers: {
        locale,
      },
    }).then((resp) => {
      if (resp.error) return this.changeFormConfigs('publishError', resp.error)
      this.changeFormConfigs('affiliateProgramJoined', true)
    })
  }

  @action setRecaptcha = (recaptcha) => (this.recaptcha = recaptcha)

  @action handleUserData = (field, value) => {
    this.userData = {
      ...this.userData,
      [field]: value,
    }
  }

  @action fetchAffiliateProgram = () => {
    const {
      affiliateProgramsStore,
      productLink: {
        affiliateNode = {},
        embedPrefs: { prid },
      },
    } = this.props
    const { affiliateProgramId } = affiliateNode
    const affiliateProgramRequestId = prid || affiliateProgramId

    if (affiliateProgramRequestId) {
      affiliateProgramsStore
        .fetchItem(affiliateProgramRequestId, {
          expand: ['covers', 'affiliate_program_commissions'],
        })
        .then((resp) => {
          this.affiliateProgram = resp.data
        })
    }
  }

  @action changeFormConfigs = (field, value) => {
    this.formConfigs = {
      ...this.formConfigs,
      [field]: value,
    }
  }

  @action handleRegistrationSubmit = () => {
    const { firstName, lastName, email, password, termsAccepted } = this.userData
    const payload = {
      email,
      password,
      profileType: PROFILE_TYPES.publisher,
      userProfilesAttributes: [
        {
          firstName,
          lastName,
        },
      ],
      publisher: {
        termsAccepted,
      },
      recaptcha: this.recaptcha,
    }
    signUp(payload).then((userResp) => {
      this.loading = true
      this.changeFormConfigs('registrationFormOpened', !this.formConfigs.registrationFormOpened)
      fetchItem({
        headers: userResp.accessToken,
      }).then((publisherResp) => {
        this.changeFormConfigs('publisherId', publisherResp.data.id)
        runInAction(() => (this.loading = false))
      })
    })
  }

  render() {
    const {
      classes,
      productLink: {
        embedPrefs,
        seller,
        product = {},
        embedPrefs: { labelsEnabled, locale, prid },
      },
    } = this.props
    const allProductForm = (this.affiliateProgram || {}).form === AFFILIATE_PROGRAM_FORMS.all
    const { commission, affiliateProgramCommissions, name, cookiesExpire = 0 } = this.affiliateProgram || {}
    const commissions = allProductForm ? commission : affiliateProgramCommissions
    const commissionRange = getCommissionRange(allProductForm, commissions, getStagesType(this.affiliateProgram))
    const { affiliateProgramJoined, publisherId, publishError, registrationFormOpened } = this.formConfigs
    const urlForPublisher = getShopProductLink(
      seller,
      product.slug,
      `?${queryString.stringify({
        pid: publisherId,
        prid,
      })}`
    )
    const formContainerClasses = classnames({
      [classes.embedFormAcceptContainer]: !affiliateProgramJoined,
      [classes.embedFormResultContainer]: affiliateProgramJoined,
    })
    return (
      <div className={classes.marginFix}>
        <div id='embed-form-wrapper' className={classes.embedFormContainer}>
          {this.loading && <div className='lmask' />}
          <div className={classes.embedFormContent}>
            <div className={classes.embedFormHeader}>
              <div className={classes.embedFormTitle}>{name}</div>
              <div className={classes.embedFormDescription}>
                <b>{I18n.t('front.widgets.embed_form.commission_for_sale', { locale })}</b>
                {`: ${commissionRange}`}
              </div>
              <div className={classes.embedFormDescription}>
                <b>{I18n.t('front.widgets.embed_form.cookie_lifetime', { locale })}</b>
                {[
                  ':',
                  I18n.t('front.widgets.embed_form.day', {
                    locale,
                    count: cookiesExpire,
                  }),
                ].join(' ')}
              </div>
            </div>
            <div className={formContainerClasses}>
              {affiliateProgramJoined ? (
                <TextField
                  label={labelsEnabled && I18n.t('front.widgets.embed_form.product_url', { locale })}
                  placeholder={I18n.t('front.widgets.embed_form.product_url', { locale })}
                  value={urlForPublisher}
                  labelClassName={classes.embedFormLabel}
                  disabled
                />
              ) : (
                <NumberField
                  label={labelsEnabled && I18n.t('front.widgets.embed_form.publisher_id', { locale })}
                  placeholder={I18n.t('front.widgets.embed_form.publisher_id', { locale })}
                  value={publisherId}
                  onChange={(value) => this.changeFormConfigs('publisherId', value)}
                  onBlur={(value) => this.changeFormConfigs('publisherId', value)}
                  labelClassName={classes.embedFormLabel}
                  errorText={I18n.t('react.shared.validations.required', { locale })}
                  required
                />
              )}
              {publishError && !affiliateProgramJoined && (
                <div className={classes.errorContainer}>
                  <ErrorInfo message={publishError} />
                </div>
              )}
              {affiliateProgramJoined ? (
                <div className={classes.buttonContainer}>
                  <button
                    onClick={() => copyTextToClipboard(urlForPublisher)}
                    className={classnames(classes.button, classes[embedPrefs.buttonAnimation])}
                    disabled={!publisherId}
                  >
                    {I18n.t('front.widgets.embed_form.copy', { locale })}
                  </button>
                </div>
              ) : (
                <div className={classes.buttonContainer}>
                  <button
                    onClick={this.handlePubIdSubmit}
                    className={classnames(classes.button, classes[embedPrefs.buttonAnimation])}
                    disabled={!publisherId}
                  >
                    {I18n.t('front.widgets.embed_form.accept', { locale })}
                  </button>
                </div>
              )}
            </div>
            {!affiliateProgramJoined &&
              (registrationFormOpened ? (
                <RegistrationForm
                  userData={this.userData}
                  handleUserData={this.handleUserData}
                  handleSubmit={this.handleRegistrationSubmit}
                  embedPrefs={embedPrefs}
                  handleCloseForm={() => this.changeFormConfigs('registrationFormOpened', !registrationFormOpened)}
                  labelsEnabled={labelsEnabled}
                  locale={locale}
                  recaptcha={this.recaptcha}
                  setRecaptcha={this.setRecaptcha}
                />
              ) : (
                <div
                  className={classes.registrationCta}
                  dangerouslySetInnerHTML={{ __html: I18n.t('front.widgets.embed_form.not_publisher_yet', { locale }) }}
                  onClick={() => this.changeFormConfigs('registrationFormOpened', !registrationFormOpened)}
                />
              ))}
          </div>
        </div>
      </div>
    )
  }
}

const StyledEmbedForm = withStyles(embeddableItemStyles)(EmbedForm)

const EmbedFormContainer = (props) => (
  <ThemeProvider theme={props.productLink.embedPrefs}>
    <StyledEmbedForm {...props} />
  </ThemeProvider>
)

export default EmbedFormContainer
